import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { normalizeLanguage } from "../../services/language";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import PuzzleBoard2D from "../../components/puzzles/PuzzleBoard2D";
import "../../components/lobby/lobbyModern.css";
import "./puzzlesV2.css";

// Página Puzzles con el diseño Clásico (estilo 2 «tarjetas como la Tienda», elegido por Yosdel
// el 2026-10-17). Cada tarjeta enseña el tablero del puzzle visto desde arriba (2D) como vista
// previa; al pulsar «Resolver» el puzzle se juega en 3D como siempre.
// Sin Premium: 1 puzzle al día (el servidor lo controla en /tournaments/puzzles/load).

const MODES = [
  { id: "all", icon: "✨", label: "Todos" },
  { id: "ciencia", icon: "🔷", label: "Ciencia" },
  { id: "cienzar", icon: "🟪", label: "Cienzar" },
  { id: "azar", icon: "🟥", label: "Azar" },
];

const STATES = [
  { id: "new", icon: "○", label: "Sin intentar" },
  { id: "failed", icon: "✗", label: "Fallados" },
  { id: "solved", icon: "✓", label: "Resueltos" },
  { id: "claim", icon: "🎁", label: "Por reclamar" },
];

const SORTS = [
  { id: "recent", label: "Más recientes" },
  { id: "oldest", label: "Más antiguos" },
  { id: "reward", label: "Mayor recompensa" },
  { id: "pending", label: "Primero sin resolver" },
];

const modeOf = (baseMode) => {
  const value = String(baseMode || "").toLowerCase();
  if (value === "cienzar") return "cienzar";
  if (value === "dice" || value === "azar") return "azar";
  return "ciencia";
};

const MODE_LABEL = { ciencia: "Ciencia", cienzar: "Cienzar", azar: "Azar" };

const formatNumber = (value) =>
  new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(Number(value) || 0)));

const formatTime = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  return `${String(minutes).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// «Puzzle 3 · Llegar a G7: Lleva tu especial a G7 en 3 jugadas.» → objetivo tras los dos puntos.
// Nombres automáticos o vacíos → la regla del puzzle (llegar a la cima en N turnos).
const buildGoal = (puzzle) => {
  const name = String(puzzle?.tournamentName || "").trim();
  const turns = Math.max(0, Number(puzzle?.puzzleTurnLimit) || 0);
  const seconds = Math.max(0, Number(puzzle?.puzzleSolveTimeLimitSeconds) || 0);
  const isAuto = !name || /^puzzle$/i.test(name) || /^(llega|reach|erreiche|доберись)/i.test(name);
  if (!isAuto) {
    const colon = name.indexOf(":");
    if (colon > 0 && colon < name.length - 2) return name.slice(colon + 1).trim();
    return name;
  }
  if (!turns || turns >= 999) return "Lleva tu bloque especial a la cima (G7).";
  const minutes = seconds > 0 ? Math.max(1, Math.ceil(seconds / 60)) : 0;
  return minutes
    ? `Llega a la cima en ${plural(turns, "turno", "turnos")} y ${plural(minutes, "minuto", "minutos")}.`
    : `Llega a la cima en ${plural(turns, "turno", "turnos")}.`;
};

const Puzzles = () => {
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const routeActions = useModernRouteActions();
  const navigate = useNavigate();
  const isAdmin = Boolean(outletContext.hasAdminMenuAccess);

  const [puzzles, setPuzzles] = useState([]);
  const [access, setAccess] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // ?modo=ciencia|cienzar|azar: abre ya filtrado (fichas del lobby En vivo).
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(() => {
    const requested = String(searchParams.get("modo") || "");
    return MODE_LABEL[requested] ? requested : "all";
  });
  const [stateFilter, setStateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [detail, setDetail] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [notice, setNotice] = useState("");

  // Igual que la Tienda: se esconde el menú antiguo y queda solo el del diseño Clásico.
  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active");
    return () => document.body.classList.remove("xima-lobby-modern-active");
  }, []);

  const loadPuzzles = useCallback(async () => {
    try {
      const response = await axios.get("/tournaments/puzzles", { params: { limit: 300 } });
      setPuzzles(Array.isArray(response?.data?.puzzles) ? response.data.puzzles : []);
      setAccess(response?.data?.access || null);
      setSummary(response?.data?.summary || null);
      setLoadError("");
    } catch (error) {
      setLoadError(error?.response?.data?.message || "No se pudo cargar la lista de puzzles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPuzzles();
  }, [loadPuzzles]);

  const isPremium = Boolean(access?.premium);
  const puzzlesRemaining = isPremium ? null : Math.max(0, Number(access?.puzzlesRemaining) || 0);
  const todayIds = useMemo(() => new Set(access?.puzzleIdsToday || []), [access]);

  // Tarjetas: número por modo (el más antiguo es el #1) y estado real del jugador.
  const entries = useMemo(() => {
    const ordered = [...puzzles].sort(
      (a, b) => new Date(a?.createdAt || 0).valueOf() - new Date(b?.createdAt || 0).valueOf()
    );
    const counters = {};
    return ordered.map((puzzle) => {
      const id = String(puzzle?.id || "");
      const puzzleMode = modeOf(puzzle?.freeBoxBaseMode);
      counters[puzzleMode] = (counters[puzzleMode] || 0) + 1;
      const stats = puzzle?.myStats || {};
      const solved = Boolean(puzzle?.solvedByCurrentUser);
      const failed = Boolean(puzzle?.rewardFailedByCurrentUser) && !solved;
      const claimed = Boolean(puzzle?.rewardClaimedByCurrentUser);
      const reward = Math.max(0, Number(puzzle?.puzzleRewardValue) || 0);
      const canClaim = solved && !puzzle?.rewardFailedByCurrentUser && !claimed && reward > 0;
      const attempts = Math.max(0, Number(stats.attempts) || 0);
      let state = "new";
      if (canClaim) state = "claim";
      else if (solved) state = "solved";
      else if (failed) state = "failed";
      else if (attempts > 0) state = "tried";
      return {
        id,
        puzzle,
        mode: puzzleMode,
        number: counters[puzzleMode],
        goal: buildGoal(puzzle),
        turnLimit: Math.max(0, Number(puzzle?.puzzleTurnLimit) || 0),
        reward,
        claimed,
        canClaim,
        solved,
        failed,
        attempts,
        bestTimeSec: Number(stats.bestTimeSec) > 0 ? Number(stats.bestTimeSec) : null,
        state,
        createdAtMs: new Date(puzzle?.createdAt || 0).valueOf() || 0,
        locked: !isPremium && access && puzzlesRemaining === 0 && !todayIds.has(id),
      };
    });
  }, [access, isPremium, puzzles, puzzlesRemaining, todayIds]);

  const modeCounts = useMemo(() => {
    const counts = { all: entries.length, ciencia: 0, cienzar: 0, azar: 0 };
    entries.forEach((entry) => {
      counts[entry.mode] += 1;
    });
    return counts;
  }, [entries]);

  const inMode = useMemo(
    () => (mode === "all" ? entries : entries.filter((entry) => entry.mode === mode)),
    [entries, mode]
  );

  const stateCounts = useMemo(() => {
    const counts = { new: 0, failed: 0, solved: 0, claim: 0 };
    inMode.forEach((entry) => {
      if (entry.state === "new") counts.new += 1;
      if (entry.failed) counts.failed += 1;
      if (entry.solved) counts.solved += 1;
      if (entry.canClaim) counts.claim += 1;
    });
    return counts;
  }, [inMode]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = inMode.filter((entry) => {
      if (stateFilter === "new" && entry.state !== "new") return false;
      if (stateFilter === "failed" && !entry.failed) return false;
      if (stateFilter === "solved" && !entry.solved) return false;
      if (stateFilter === "claim" && !entry.canClaim) return false;
      if (!term) return true;
      return `puzzle #${entry.number} ${MODE_LABEL[entry.mode]} ${entry.goal}`.toLowerCase().includes(term);
    });
    const rank = { claim: 0, new: 1, tried: 2, failed: 3, solved: 4 };
    return list.sort((a, b) => {
      if (sort === "oldest") return a.createdAtMs - b.createdAtMs;
      if (sort === "reward") return b.reward - a.reward || b.createdAtMs - a.createdAtMs;
      if (sort === "pending") return rank[a.state] - rank[b.state] || b.createdAtMs - a.createdAtMs;
      return b.createdAtMs - a.createdAtMs;
    });
  }, [inMode, search, sort, stateFilter]);

  const total = Number(summary?.total) || entries.length;
  const solvedCount = Number(summary?.solved) || 0;
  const playedCount = Number(summary?.played) || 0;
  const successRate = playedCount ? Math.round((solvedCount / playedCount) * 100) : 0;
  const progressPercent = total ? Math.min(100, (solvedCount / total) * 100) : 0;

  const startPuzzle = useCallback(
    async (entry) => {
      if (!entry?.id) return;
      setBusyId(entry.id);
      try {
        await axios.post("/replays/clear");
      } catch (_error) {
        // Igual que antes: si falla limpiar la repetición, se abre el puzzle de todos modos.
      }
      navigate(`/game/${entry.id}?autoplay=1&puzzle=1`);
    },
    [navigate]
  );

  // Abrir un puzzle: sin Premium se avisa antes de gastar el puzzle gratis del día.
  const requestStart = useCallback(
    (entry) => {
      if (!entry) return;
      if (entry.locked) {
        setDialog({ type: "premium" });
        return;
      }
      if (!isPremium && access && !todayIds.has(entry.id)) {
        setDialog({ type: "confirm", entry });
        return;
      }
      startPuzzle(entry);
    },
    [access, isPremium, startPuzzle, todayIds]
  );

  const claimReward = useCallback(
    async (entry) => {
      if (!entry?.canClaim || busyId) return;
      setBusyId(entry.id);
      try {
        const response = await axios.post("/tournaments/puzzles/claim-reward", { puzzleId: entry.id });
        const value = Number(response?.data?.rewardValue) || entry.reward;
        setNotice(`🎁 +${formatNumber(value)} X añadidos a tus bonos.`);
      } catch (error) {
        const code = String(error?.response?.data?.code || "").toUpperCase();
        setNotice(
          code === "GUEST_REGISTER_REQUIRED"
            ? "Regístrate para reclamar recompensas."
            : error?.response?.data?.message || "No se pudo reclamar la recompensa."
        );
      } finally {
        setBusyId("");
        loadPuzzles();
      }
    },
    [busyId, loadPuzzles]
  );

  const renderAction = (entry, large = false) => {
    const size = large ? " is-large" : "";
    if (busyId === entry.id) {
      return (
        <button type="button" className={`xpz-btn is-ghost${size}`} disabled>
          Abriendo…
        </button>
      );
    }
    if (entry.canClaim) {
      return (
        <button type="button" className={`xpz-btn is-claim${size}`} onClick={() => claimReward(entry)}>
          Reclamar {formatNumber(entry.reward)} X
        </button>
      );
    }
    if (entry.locked) {
      return (
        <button type="button" className={`xpz-btn is-locked${size}`} onClick={() => setDialog({ type: "premium" })}>
          🔒 Premium
        </button>
      );
    }
    if (entry.solved) {
      return (
        <button type="button" className={`xpz-btn is-ghost${size}`} onClick={() => requestStart(entry)}>
          Jugar otra vez
        </button>
      );
    }
    if (entry.failed || entry.attempts > 0) {
      return (
        <button type="button" className={`xpz-btn is-ghost${size}`} onClick={() => requestStart(entry)}>
          Reintentar
        </button>
      );
    }
    return (
      <button type="button" className={`xpz-btn is-gold${size}`} onClick={() => requestStart(entry)}>
        Resolver
      </button>
    );
  };

  const renderBadge = (entry) => {
    if (entry.state === "claim") return <span className="xpz-badge is-claim">🎁 Por reclamar</span>;
    if (entry.state === "solved") return <span className="xpz-badge is-solved">✓ Resuelto</span>;
    if (entry.state === "failed") return <span className="xpz-badge is-failed">✗ Fallado</span>;
    if (entry.state === "tried") return <span className="xpz-badge is-tried">Intentado</span>;
    return <span className="xpz-badge is-new">Nuevo</span>;
  };

  const renderStats = (entry) => (
    <div className="xpz-stats">
      <span title="Límite de turnos del puzzle">
        🎯 {entry.turnLimit && entry.turnLimit < 999 ? plural(entry.turnLimit, "turno", "turnos") : "Sin límite"}
      </span>
      <span title="Tus intentos">🔁 {entry.attempts ? plural(entry.attempts, "intento", "intentos") : "—"}</span>
      <span title="Tu mejor tiempo resolviéndolo">⏱ {entry.bestTimeSec ? formatTime(entry.bestTimeSec) : "—"}</span>
    </div>
  );

  const rewardLabel = (entry) => {
    if (entry.failed && !entry.solved) return <span className="xpz-reward is-lost">Sin bono</span>;
    if (entry.claimed) return <span className="xpz-reward is-claimed">✓ {formatNumber(entry.reward)} X</span>;
    return <span className="xpz-reward">{formatNumber(entry.reward)} X</span>;
  };

  return (
    <div className="xlm-root xpz-root">
      <ModernSidebar
        language={language}
        active="puzzles"
        badges={outletContext.sidebarBadges || {}}
        isAdmin={isAdmin}
        actions={routeActions}
      />
      <div className="xlm-main xpz-main">
        <div className="xpz-shell">
          <header className="xpz-top">
            <div className="xpz-title">
              <button type="button" className="xpz-back" onClick={() => navigate("/game/lobby")}>
                ‹ Volver al Lobby
              </button>
              <h1>Puzzles XIMA</h1>
              <p>Resuelve situaciones reales de partida y gana bonos.</p>
            </div>
            <div className="xpz-kpis">
              <div className="xpz-kpi">
                <b>
                  {formatNumber(solvedCount)}
                  <small> / {formatNumber(total)}</small>
                </b>
                <span>Resueltos</span>
              </div>
              <div className="xpz-kpi" title="Puzzles resueltos de los que has jugado">
                <b>{playedCount ? `${successRate} %` : "—"}</b>
                <span>Éxito</span>
              </div>
              <div className="xpz-kpi" title="Días seguidos resolviendo al menos un puzzle">
                <b>{formatNumber(summary?.streakDays)} 🔥</b>
                <span>Racha</span>
              </div>
              <div className="xpz-kpi is-gold" title="Bonos que ya reclamaste en puzzles">
                <b>{formatNumber(summary?.rewardsClaimed)} X</b>
                <span>Bonos</span>
              </div>
            </div>
          </header>

          {access && !isPremium ? (
            <section className="xpz-banner">
              <span className="xpz-banner-icon" aria-hidden="true">
                💎
              </span>
              <div className="xpz-banner-text">
                {puzzlesRemaining > 0 ? (
                  <strong>
                    Te queda <em>1 puzzle gratis</em> hoy
                  </strong>
                ) : (
                  <strong>Ya usaste tu puzzle gratis de hoy</strong>
                )}
                <p>
                  {puzzlesRemaining > 0
                    ? "Se renueva a medianoche. Con Premium resuelves todos los puzzles que quieras."
                    : "Puedes seguir con el de hoy. Mañana tendrás otro gratis, o hazte Premium para resolver todos los que quieras."}
                </p>
              </div>
              <button
                type="button"
                className={`xpz-btn ${puzzlesRemaining > 0 ? "is-ghost" : "is-gold"}`}
                onClick={() => navigate("/game/premium")}
              >
                {puzzlesRemaining > 0 ? "Ver Premium" : "⭐ Hazte Premium"}
              </button>
            </section>
          ) : null}
          {access && isPremium ? (
            <p className="xpz-premium-note">⭐ {access.admin ? "Administrador" : "Premium"}: puzzles sin límite.</p>
          ) : null}

          {notice ? (
            <button type="button" className="xpz-notice" onClick={() => setNotice("")}>
              {notice} <span aria-hidden="true">✕</span>
            </button>
          ) : null}

          <div className="xpz-layout">
            <aside className="xpz-side">
              <div className="xpz-side-label">Modo</div>
              {MODES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`xpz-pill${mode === item.id ? " is-active" : ""}`}
                  onClick={() => setMode(item.id)}
                >
                  <span>
                    <i aria-hidden="true">{item.icon}</i> {item.label}
                  </span>
                  <small>{modeCounts[item.id] || 0}</small>
                </button>
              ))}
              <div className="xpz-side-label">Estado</div>
              {STATES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`xpz-pill${stateFilter === item.id ? " is-active" : ""}`}
                  onClick={() => setStateFilter((current) => (current === item.id ? "" : item.id))}
                >
                  <span>
                    <i aria-hidden="true">{item.icon}</i> {item.label}
                  </span>
                  <small>{stateCounts[item.id] || 0}</small>
                </button>
              ))}
              <div className="xpz-progress">
                <strong>Progreso</strong>
                <div className="xpz-bar">
                  <i style={{ width: `${progressPercent}%` }} />
                </div>
                <span>
                  {formatNumber(solvedCount)} de {formatNumber(total)} puzzles
                </span>
              </div>
              {isAdmin ? (
                <button
                  type="button"
                  className="xpz-admin-link"
                  onClick={() => navigate("/game?quickView=daily-puzzles&legacy=1")}
                >
                  ✏️ Editar puzzles (vista antigua)
                </button>
              ) : null}
            </aside>

            <section className="xpz-content">
              <div className="xpz-content-head">
                <h2>
                  {mode === "all" ? "Todos los puzzles" : `Puzzles de ${MODE_LABEL[mode]}`}
                  <span> · {visible.length}</span>
                </h2>
                <div className="xpz-tools">
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="🔍 Buscar puzzle…"
                    aria-label="Buscar puzzle"
                  />
                  <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Ordenar">
                    {SORTS.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {loading ? <div className="xpz-empty">Cargando puzzles…</div> : null}
              {!loading && loadError ? <div className="xpz-empty">{loadError}</div> : null}
              {!loading && !loadError && !visible.length ? (
                <div className="xpz-empty">No hay puzzles con este filtro.</div>
              ) : null}

              <div className="xpz-grid">
                {visible.map((entry) => (
                  <article key={entry.id} className={`xpz-card is-${entry.mode}`}>
                    <div className="xpz-card-top">
                      <span className={`xpz-mode is-${entry.mode}`}>{MODE_LABEL[entry.mode]}</span>
                      {renderBadge(entry)}
                    </div>
                    <button
                      type="button"
                      className="xpz-board-btn"
                      onClick={() => setDetail(entry)}
                      title="Ver el puzzle en grande"
                    >
                      {entry.puzzle?.board2d ? (
                        <PuzzleBoard2D board={entry.puzzle.board2d} size={176} />
                      ) : (
                        <div className="xpz-board-missing">Vista previa no disponible</div>
                      )}
                    </button>
                    <h3>Puzzle #{entry.number}</h3>
                    <p className="xpz-goal">{entry.goal}</p>
                    {renderStats(entry)}
                    <div className="xpz-card-foot">
                      {rewardLabel(entry)}
                      {renderAction(entry)}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      {detail ? (
        <div className="xpz-modal" role="dialog" aria-modal="true" onClick={() => setDetail(null)}>
          <div className="xpz-modal-card is-detail" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="xpz-modal-close" onClick={() => setDetail(null)} aria-label="Cerrar">
              ✕
            </button>
            <div className="xpz-detail-board">
              {detail.puzzle?.board2d ? (
                <PuzzleBoard2D board={detail.puzzle.board2d} size={420} className="is-large" />
              ) : (
                <div className="xpz-board-missing is-large">Vista previa no disponible</div>
              )}
            </div>
            <div className="xpz-detail-info">
              <div className="xpz-card-top">
                <span className={`xpz-mode is-${detail.mode}`}>{MODE_LABEL[detail.mode]}</span>
                {renderBadge(detail)}
              </div>
              <h3>Puzzle #{detail.number}</h3>
              <p className="xpz-goal is-full">{detail.goal}</p>
              {renderStats(detail)}
              <p className="xpz-detail-hint">
                Esta es la posición vista desde arriba. El puzzle se juega en el tablero 3D. Tú juegas con los bloques
                azules; el bloque con ★ es el especial.
              </p>
              <div className="xpz-card-foot">
                {rewardLabel(detail)}
                {renderAction(detail, true)}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {dialog ? (
        <div className="xpz-modal" role="dialog" aria-modal="true" onClick={() => setDialog(null)}>
          <div className="xpz-modal-card is-small" onClick={(event) => event.stopPropagation()}>
            {dialog.type === "confirm" ? (
              <>
                <span className="xpz-dialog-icon" aria-hidden="true">
                  🧩
                </span>
                <h3>¿Usar tu puzzle gratis de hoy?</h3>
                <p>
                  Sin Premium puedes jugar 1 puzzle al día. Si empiezas el Puzzle #{dialog.entry.number}, podrás
                  repetirlo todas las veces que quieras hoy, pero no abrir otro hasta mañana.
                </p>
                <div className="xpz-dialog-actions">
                  <button type="button" className="xpz-btn is-ghost" onClick={() => setDialog(null)}>
                    Ahora no
                  </button>
                  <button
                    type="button"
                    className="xpz-btn is-gold"
                    onClick={() => {
                      const { entry } = dialog;
                      setDialog(null);
                      setDetail(null);
                      startPuzzle(entry);
                    }}
                  >
                    Empezar puzzle
                  </button>
                </div>
              </>
            ) : (
              <>
                <span className="xpz-dialog-icon" aria-hidden="true">
                  💎
                </span>
                <h3>Ya usaste tu puzzle gratis de hoy</h3>
                <p>
                  Resolver más puzzles es una función Premium. Hazte miembro Premium para jugar todos los puzzles que
                  quieras, o vuelve mañana por otro gratis.
                </p>
                <div className="xpz-dialog-actions">
                  <button type="button" className="xpz-btn is-ghost" onClick={() => setDialog(null)}>
                    Volver mañana
                  </button>
                  <button type="button" className="xpz-btn is-gold" onClick={() => navigate("/game/premium")}>
                    ⭐ Hazte Premium
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Puzzles;
